import { PriorityLevel } from '@sib/api';
import { DEFAULT_DATE_FORMAT, NO_DATA_PLACEHOLDER } from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { Badge, Button, Person, Tooltip } from '@sib/ui';
import { usePriorityOptions } from '@sib/ui/hooks';
import { Edit } from '@sib/ui/icons';
import { priorityTagClassNames } from '@sib/utils';
import { GuardedTaskCheckbox } from '@sidecar/common/components/GuardedTaskCheckbox';
import { format } from 'date-fns';

export interface TaskItemCardAssignee {
  name?: string;
  email?: string | null;
  avatarUrl?: string | null;
}

interface TaskItemCardProps {
  isComplete: boolean;
  title: string;
  description?: string | null;
  priorityLevel?: PriorityLevel | null;
  assignee?: TaskItemCardAssignee | null;
  dueDate?: string | null;
  canEdit?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  checkboxId?: string;
  /** Accessible label for the edit button. */
  editLabel?: string;
  onToggle?: () => void;
  /** Renders an edit button when provided. */
  onEdit?: () => void;
}

export const TaskItemCard = ({
  isComplete,
  title,
  description,
  priorityLevel,
  assignee,
  dueDate,
  canEdit = true,
  disabled,
  disabledTooltip,
  checkboxId,
  editLabel,
  onToggle,
  onEdit,
}: TaskItemCardProps) => {
  const { t } = useTranslation();
  const priorityOptions = usePriorityOptions();
  const priorityOption = priorityOptions.find(
    option => option.value === priorityLevel
  );

  const editButton = onEdit && (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="size-8 shrink-0"
      aria-label={editLabel}
      disabled={!canEdit}
      onClick={onEdit}
    >
      <Edit className="size-4" />
    </Button>
  );

  return (
    <div className="border-border flex flex-col gap-3 rounded-md border p-4 shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <GuardedTaskCheckbox
            id={checkboxId}
            checked={isComplete}
            canEdit={canEdit}
            disabled={disabled}
            disabledTooltip={disabledTooltip}
            className="mt-0.5"
            onToggle={onToggle}
          />
          <span className="font-semibold">{title}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {priorityOption && priorityLevel && (
            <Badge
              variant="outline"
              className={`shrink-0 gap-1 rounded-md ${priorityTagClassNames[priorityLevel]}`}
            >
              {priorityOption.icon}
              {priorityOption.label}
            </Badge>
          )}
          {editButton && !canEdit && disabledTooltip ? (
            <Tooltip
              content={disabledTooltip}
              TooltipTriggerProps={{ asChild: true }}
            >
              <span>{editButton}</span>
            </Tooltip>
          ) : (
            editButton
          )}
        </div>
      </div>
      <p className="text-muted-foreground line-clamp-3 text-sm">
        {description}
      </p>
      {(assignee || dueDate) && (
        <>
          <hr className="border-border" />
          <div className="flex items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs">
                {t('components.taskDetails.subtasks.card.assignee')}
              </span>
              {assignee ? <Person {...assignee} /> : t('unassigned')}
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-muted-foreground text-xs">
                {t('components.taskDetails.subtasks.card.dueDate')}
              </span>
              <span className="text-sm font-semibold">
                {dueDate
                  ? format(new Date(dueDate), DEFAULT_DATE_FORMAT)
                  : NO_DATA_PLACEHOLDER}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
